import { lazy, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
// HashRouter is required for Electron — BrowserRouter reads window.location.pathname
// which under file:// protocol is the full disk path, not "/". HashRouter reads
// window.location.hash (#/clients) which always starts from the root.
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// UnlockScreen is the pre-router gate (rendered before <Router> mounts in the
// Electron lock flow). It stays eagerly imported so the lock screen never
// flashes a Suspense fallback — it's small and always needed first.
import UnlockScreen from './screens/UnlockScreen';
// Route-level code splitting — every routed screen is React.lazy so the
// initial bundle ships only the shell (router + layout + sidebar). Each
// screen's chunk loads on first navigation to it. The big wins are the
// heavy editor screens (ReportBuilder, TemplateEditor, Findings) which most
// sessions never open. The <Suspense> boundary that renders the loading
// fallback while a chunk is in flight lives in MainLayout, wrapped around the
// <Outlet /> so the sidebar stays mounted during a cold-chunk navigation.
const ClientsScreen        = lazy(() => import('./screens/ClientsScreen'));
const EngagementsScreen    = lazy(() => import('./screens/EngagementsScreen'));
const FindingsScreen       = lazy(() => import('./screens/FindingsScreen'));
const ReportBuilderScreen  = lazy(() => import('./screens/ReportBuilderScreen'));
const ReportDispatchScreen = lazy(() => import('./screens/ReportDispatchScreen'));
const LibraryScreen        = lazy(() => import('./screens/LibraryScreen'));
const TemplatesScreen      = lazy(() => import('./screens/TemplatesScreen'));
const TemplateEditorScreen = lazy(() => import('./screens/TemplateEditorScreen'));
const AuditLogScreen       = lazy(() => import('./screens/AuditLogScreen'));
const FirmSettingsScreen   = lazy(() => import('./screens/FirmSettingsScreen'));
const ProfileScreen        = lazy(() => import('./screens/ProfileScreen'));
const MembersScreen        = lazy(() => import('./screens/MembersScreen'));
const MyScheduleScreen     = lazy(() => import('./screens/MyScheduleScreen'));
const ScheduleAdminScreen  = lazy(() => import('./screens/ScheduleAdminScreen'));
const BillingScreen        = lazy(() => import('./screens/BillingScreen'));
import MainLayout from './layouts/MainLayout';
import { SigningOutSplash } from './components/SigningOutSplash';
import { invoke, onPush } from './lib/ipc';
import { clearAllBrowserStorage } from './lib/safe-storage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RequireRole, type Role } from './components/RequireRole';

// Workspace screens — both supported roles (firm_admin + consultant) can
// reach the full workspace. 2-role model post 2026-06-29 RBAC collapse.
const WORKSPACE_ROLES: Role[] = ['firm_admin', 'consultant'];

// True when running inside Electron (the preload script installs window.cipherops).
// In the web shell this is always false, so we skip the local-password unlock
// gate — auth is handled upstream by the WorkOS-backed AuthGate before this
// component ever renders.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_ELECTRON = typeof window !== 'undefined' && (window as any).cipherops != null;

export function AppRouter() {
  // Web shell starts unlocked (auth was already verified by AuthGate).
  // Electron starts locked (UnlockScreen handles Argon2 password unlock).
  const [isUnlocked, setIsUnlocked] = useState(!IS_ELECTRON);
  const [isFirstRun, setIsFirstRun]  = useState(false);
  // Web sign-out cover — set synchronously (flushSync) the instant Sign Out is
  // clicked so the app is hidden before the async logout + WorkOS redirect run.
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    if (!IS_ELECTRON) return;
    invoke<{ first_run: boolean }>('cipherops/auth/is_first_run')
      .then((data) => setIsFirstRun(data.first_run))
      .catch(() => { /* IPC channel unavailable — leave defaults */ });
  }, []);

  useEffect(() => {
    if (!IS_ELECTRON) return;
    const unsub = onPush('cipherops/system/auto_lock', () => {
      setIsUnlocked(false);
    });
    return unsub;
  }, []);

  const handleUnlock = () => {
    setIsFirstRun(false);
    setIsUnlocked(true);
  };

  const handleLock = async () => {
    if (IS_ELECTRON) {
      try {
        await invoke('cipherops/auth/lock');
      } catch {
        /* ignore errors -- still lock the UI */
      }
      setIsUnlocked(false);
    } else {
      // Cover the app immediately so the user never sees it linger during the
      // async logout + WorkOS redirect below. flushSync forces the splash to
      // paint before the first await, otherwise React could batch the render
      // until after the round-trip — exactly the transient we're fixing.
      flushSync(() => setIsSigningOut(true));
      // Web shell sign-out — proper full-stack termination:
      //   1. Server clears local session cookie + returns the WorkOS
      //      logout URL (server reads the WorkOS sessionId we captured
      //      during the original /auth/callback). Without going through
      //      WorkOS's logout endpoint, the upstream session stays valid
      //      and any /auth/login attempt silently re-issues a session —
      //      so it looks like sign-out did nothing (issue #70).
      //   2. Clear all browser-side state — localStorage, sessionStorage,
      //      and any non-httpOnly cookies on the current domain — so a
      //      different account signing in next inherits nothing.
      //   3. Hard navigate to the WorkOS logout URL. WorkOS terminates
      //      its own session, then redirects back to our registered
      //      post-logout URI. At that point the user is fully signed out
      //      and re-entering the app requires fresh credentials + MFA.
      let redirect = '/auth/login';
      try {
        const result = await invoke<{ ok: boolean; redirect?: string }>('cipherops/auth/logout');
        if (result?.redirect) redirect = result.redirect;
      } catch { /* ignore — still navigate so user isn't stuck */ }
      // Wipe localStorage + sessionStorage + non-httpOnly cookies. The
      // httpOnly session cookie is already cleared server-side in step 1.
      // Centralised in safe-storage.ts so the policy stays consistent
      // across sign-out paths.
      clearAllBrowserStorage();
      window.location.href = redirect;
    }
  };

  // Signing out — cover everything (takes precedence over the unlock gate).
  if (isSigningOut) {
    return <SigningOutSplash />;
  }

  if (!isUnlocked) {
    return <UnlockScreen onUnlock={handleUnlock} isFirstRun={isFirstRun} />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainLayout onLock={handleLock} />}>
          {/* Landing route — /engagements is universal (everyone has access).
             Was /clients but that's now admin-only after the Option C split. */}
          <Route index element={<Navigate to="/engagements" replace />} />
          {/*
            Option C — Clients are split across two surfaces:
              Admin > Clients          → /clients          (firm_admin only, full CRUD)
              Workspace > Directory    → /client-directory (everyone, read-only repository)
            Same screen component, `mode` prop controls header copy + the
            Show Archived toggle. Role-gated controls inside the screen
            (canManageClients) drive Edit/Archive/Delete affordances.
          */}
          <Route path="clients" element={<RequireRole allow={['firm_admin']}><ErrorBoundary screenName="Clients"><ClientsScreen mode="admin" /></ErrorBoundary></RequireRole>} />
          <Route path="client-directory" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Client Directory"><ClientsScreen mode="directory" /></ErrorBoundary></RequireRole>} />
          <Route path="engagements" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Engagements"><EngagementsScreen /></ErrorBoundary></RequireRole>} />
          <Route path="engagements/:engagementId/findings" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Findings"><FindingsScreen /></ErrorBoundary></RequireRole>} />
          <Route path="report-builder" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Report Builder"><ReportBuilderScreen /></ErrorBoundary></RequireRole>} />
          <Route path="report-dispatch" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Report Analytics"><ReportDispatchScreen /></ErrorBoundary></RequireRole>} />
          <Route path="library" element={<RequireRole allow={WORKSPACE_ROLES}><ErrorBoundary screenName="Library"><LibraryScreen /></ErrorBoundary></RequireRole>} />
          {/* Templates moved to the Admin group — firm-wide brand /
              section-visibility decisions, not per-consultant. */}
          <Route path="templates" element={<RequireRole allow={['firm_admin']}><ErrorBoundary screenName="Templates"><TemplatesScreen /></ErrorBoundary></RequireRole>} />
          {/* New-template editor — nothing is persisted until Save (avoids
              phantom "Untitled" rows from just clicking New Template). */}
          <Route path="templates/new" element={<RequireRole allow={['firm_admin']}><ErrorBoundary screenName="Template Editor"><TemplateEditorScreen /></ErrorBoundary></RequireRole>} />
          <Route path="templates/:templateId/edit" element={<RequireRole allow={['firm_admin']}><ErrorBoundary screenName="Template Editor"><TemplateEditorScreen /></ErrorBoundary></RequireRole>} />
          <Route path="audit-log" element={
            <RequireRole allow={['firm_admin']}>
              <ErrorBoundary screenName="Audit Log"><AuditLogScreen /></ErrorBoundary>
            </RequireRole>
          } />
          <Route path="settings" element={
            <RequireRole allow={['firm_admin']}>
              <ErrorBoundary screenName="Settings"><FirmSettingsScreen /></ErrorBoundary>
            </RequireRole>
          } />
          <Route path="profile" element={<ErrorBoundary screenName="Profile"><ProfileScreen /></ErrorBoundary>} />
          <Route path="members" element={
            <RequireRole allow={['firm_admin']}>
              <ErrorBoundary screenName="Members"><MembersScreen /></ErrorBoundary>
            </RequireRole>
          } />
          <Route path="my-schedule" element={<ErrorBoundary screenName="My Schedule"><MyScheduleScreen /></ErrorBoundary>} />
          <Route path="schedule" element={
            <RequireRole allow={['firm_admin']}>
              <ErrorBoundary screenName="Schedule"><ScheduleAdminScreen /></ErrorBoundary>
            </RequireRole>
          } />
          <Route path="sales" element={
            <RequireRole allow={['firm_admin']}>
              <ErrorBoundary screenName="Sales"><BillingScreen /></ErrorBoundary>
            </RequireRole>
          } />
          {/* Legacy alias — bookmarks and audit-log breadcrumbs pointing at
              /billing still resolve. Safe to remove in a future cleanup. */}
          <Route path="billing" element={<Navigate to="/sales" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default AppRouter;
